/**
 * Admin Page - Refactored
 * Main entry point for the admin panel with dynamic RBAC
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Coffee, ShoppingBag, ShoppingCart, MessageSquare, MessageSquareOff, Users, 
  Mail, Clock, ChevronRight, FileText, Settings, QrCode, Sparkles, Sliders, Megaphone, Phone, 
  Image, Package, BarChart3, Bell, Contact, Ticket, Scale, ChefHat, Sun, Moon, Monitor, 
  ExternalLink, Menu as MenuIcon, Receipt, TrendingUp, Brain, Wallet, FileSpreadsheet, 
  DollarSign, Tablet, Layout, Lock, Crown, Handshake, UserPlus, Star, Table, Printer, Building2,
  UtensilsCrossed, Globe, PieChart, X, Share2, Eye, PiggyBank, ClipboardList, CalendarDays, 
  Timer, Umbrella, Trash2, Smartphone, AlertTriangle, Wifi
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

// Layout Components
import { AdminSidebar, AdminContent } from '@/components/admin/layout';

// Context
import { PermissionsProvider } from '@/context/PermissionsContext';
import { useTheme } from '@/context/ThemeContext';

// Inventory Modal
import { MenuItemModal } from '@/components/admin/inventory';

import { api } from '@/utils/apiClient';

// Hierarchical navigation structure
const NAV_SECTIONS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    children: []
  },
  {
    id: 'food',
    label: 'Food',
    icon: UtensilsCrossed,
    children: [
      { id: 'menu', label: 'Menu', icon: Coffee },
      { id: 'orders', label: 'Orders', icon: ShoppingBag },
      { id: 'build-your-own', label: 'Build Your Own', icon: ChefHat },
      { id: 'ai-verification', label: 'Order Verification', icon: Brain },
      { id: 'customizations', label: 'Add-Ons', icon: Sliders },
      { id: 'popular-customizations', label: 'Popular Mods', icon: TrendingUp },
      { id: 'specials', label: 'Specials', icon: Sparkles },
      { id: 'inventory', label: 'Inventory', icon: Package },
      { id: 'floor-status', label: 'Floor Status', icon: Table },
      { id: 'alerts', label: 'Stock Alerts', icon: Bell },
      { id: 'cart-recovery', label: 'Cart Recovery', icon: ShoppingCart },
      { id: 'upsells', label: 'Upsells', icon: Sparkles },
      { id: 'display-management', label: 'Window Displays', icon: Monitor },
    ]
  },
  {
    id: 'cms',
    label: 'CMS',
    icon: Users,
    children: [
      { id: 'contacts', label: 'All Contacts', icon: Contact },
      { id: 'founding-members', label: 'Founding Members', icon: Crown },
      { id: 'coffee-club', label: 'Coffee Pass', icon: Coffee },
      { id: 'catering', label: 'Catering', icon: UtensilsCrossed },
      { id: 'staff-management', label: 'Staff Accounts', icon: Users },
      { id: 'staff-performance', label: 'Performance Reviews', icon: ClipboardList },
      { id: 'partners', label: 'Partners (Legacy)', icon: Handshake },
      { id: 'referral-partners', label: 'Referral Partners', icon: DollarSign },
      { id: 'sales-reps', label: 'Sales Reps', icon: UserPlus },
      { id: 'users', label: 'Admin Users', icon: Users },
      { id: 'role-permissions', label: 'Role Permissions', icon: Lock, ownerOnly: true },
    ]
  },
  {
    id: 'stations',
    label: 'Stations',
    icon: Tablet,
    children: [
      { id: 'device-management', label: 'Device Registry', icon: Tablet },
      { id: 'staff-pins', label: 'Staff PINs', icon: Lock },
      { id: 'table-management', label: 'Tables', icon: Table },
      { id: 'order-timing', label: 'Order Timing', icon: Clock },
      { id: 'supervisor-sop', label: 'Supervisor SOP', icon: ClipboardList },
    ]
  },
  {
    id: 'workforce',
    label: 'Workforce',
    icon: CalendarDays,
    children: [
      { id: 'time-clock', label: 'Time Clock', icon: Timer },
      { id: 'scheduling', label: 'Scheduling', icon: CalendarDays },
      { id: 'time-off', label: 'Time Off', icon: Umbrella },
      { id: 'payroll', label: 'Payroll', icon: DollarSign },
    ]
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    children: [
      { id: 'receipt-settings', label: 'Receipts', icon: Receipt },
      { id: 'kiosk-settings', label: 'Kiosk Settings', icon: Tablet },
      { id: 'printer-routing', label: 'Printer Routing', icon: Printer },
      { id: 'kitchen-display', label: 'Kitchen Display', icon: ChefHat },
      { id: 'cash-drawer', label: 'Cash Drawer', icon: Wallet },
      { id: 'alert-notifications', label: 'Alert Notifications', icon: Bell },
      { id: 'email-notifications', label: 'Email Notifications', icon: Mail },
      { id: 'sms', label: 'SMS', icon: Phone },
      { id: 'sms-optout', label: 'SMS Opt-Out', icon: MessageSquareOff },
    ]
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: DollarSign,
    children: [
      { id: 'unified-sales', label: 'Unified Sales', icon: BarChart3 },
      { id: 'owner-audit', label: '👑 Owner Orders', icon: Crown, ownerOnly: true },
      { id: 'mpaisa-reconciliation', label: 'M-PAiSA Reconciliation', icon: Smartphone },
      { id: 'data-cleanup', label: '🔥 Data Cleanup', icon: Trash2 },
      { id: 'cancellations', label: 'Cancellations & Refunds', icon: X },
      { id: 'mistake-losses', label: 'Staff Mistakes', icon: AlertTriangle },
      { id: 'menu-performance', label: 'Menu Item Sales', icon: UtensilsCrossed },
      { id: 'sales-report', label: 'Sales Report', icon: BarChart3 },
      { id: 'sales-reconciliation', label: 'Daily Reconciliation', icon: Receipt },
      { id: 'investor-ready', label: 'Investor Dashboard', icon: BarChart3 },
      { id: 'operations-dashboard', label: 'Operations', icon: Settings },
      { id: 'cost-control', label: 'Cost Control', icon: PieChart },
      { id: 'startup-investment', label: 'Startup & ROI', icon: PiggyBank },
      { id: 'shift-reconciliation', label: 'Shift Reconciliation', icon: Wallet },
      { id: 'expense-tracking', label: 'Expenses & P/L', icon: Receipt },
      { id: 'vendor-management', label: 'Vendors', icon: Building2 },
      { id: 'detailed-inventory', label: 'Inventory', icon: Package },
      { id: 'business-valuation', label: 'Valuation', icon: TrendingUp },
    ]
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    children: [
      { id: 'wifi-settings', label: 'WiFi Portal', icon: Wifi },
      { id: 'viral-marketing', label: 'Viral Marketing', icon: Share2 },
      { id: 'referral-campaign', label: 'Referral Campaign', icon: Users },
      { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
      { id: 'automated-campaigns', label: 'Automated Campaigns', icon: Sparkles },
      { id: 'email-automation', label: 'Email Automation', icon: Mail },
      { id: 'aeo-checklist', label: 'AI Search (AEO)', icon: BarChart3 },
      { id: 'google-reviews', label: 'Google Reviews', icon: Star },
      { id: 'promo-codes', label: 'Promo Codes', icon: Ticket },
      { id: 'qrcode', label: 'QR Code', icon: QrCode },
      { id: 'messages', label: 'Messages', icon: MessageSquare },
    ]
  },
  {
    id: 'content',
    label: 'Site Manager',
    icon: Globe,
    children: [
      { id: 'legal-pages', label: 'Legal Pages', icon: Scale },
      { id: 'contact-settings', label: 'Contact Info', icon: Phone },
      { id: 'blog', label: 'Blog', icon: FileText },
      { id: 'layout-designer', label: 'Layout Designer', icon: Layout },
      { id: 'media', label: 'Media', icon: Image },
    ]
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: PieChart,
    children: [
      { id: 'daily-insights', label: 'Daily Insights', icon: Eye },
      { id: 'analytics', label: 'Analytics', icon: BarChart3 },
      { id: 'fiscal-dashboard', label: 'FRCS Fiscal', icon: QrCode },
      { id: 'accounting-export', label: 'Accounting', icon: FileSpreadsheet },
      { id: 'comparative-reports', label: 'Compare Periods', icon: TrendingUp },
      { id: 'automated-reports', label: 'Email Reports', icon: Mail },
      { id: 'profitability', label: 'Profitability', icon: DollarSign },
      { id: 'manager-overrides', label: 'Override Audit', icon: Lock },
      { id: 'royalty-report', label: '4% Royalty', icon: Building2, ownerOnly: true },
      { id: 'activity-log', label: 'Activity Log', icon: Clock },
    ]
  },
];

// Flatten all tabs for tab lookup
const getAllTabs = () => {
  const allTabs = [];
  NAV_SECTIONS.forEach(section => {
    if (section.children.length === 0) {
      allTabs.push({ id: section.id, label: section.label, icon: section.icon });
    } else {
      section.children.forEach(child => allTabs.push(child));
    }
  });
  return allTabs;
};

const DEFAULT_TABS = getAllTabs();

export default function AdminPage() {
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Admin state
  const [admin, setAdmin] = useState(() => {
    const saved = localStorage.getItem('bulaccino-admin');
    return saved ? JSON.parse(saved) : null;
  });
  
  // Theme preview state
  const [previewMode, setPreviewMode] = useState(null);
  const [originalDarkMode, setOriginalDarkMode] = useState(null);
  
  // Dynamic categories from database
  const [categories, setCategories] = useState([]);
  const [categoryLabels, setCategoryLabels] = useState({});
  
  // Active tab from URL
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState(() => {
    if (urlTab && DEFAULT_TABS.some(t => t.id === urlTab)) {
      return urlTab;
    }
    return 'dashboard';
  });
  
  // Update URL when tab changes
  const setActiveTab = useCallback((tabId) => {
    setActiveTabState(tabId);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabId);
    if (tabId !== 'menu') {
      newParams.delete('category');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState(() => {
    const initialExpanded = {};
    NAV_SECTIONS.forEach(section => {
      if (section.children.length > 0) {
        const hasActiveChild = section.children.some(child => child.id === urlTab);
        initialExpanded[section.id] = hasActiveChild;
      }
    });
    return initialExpanded;
  });
  
  // Login state
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Data states
  const [menuItems, setMenuItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);

  // Modal states
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [defaultMenuCategory, setDefaultMenuCategory] = useState('coffee');

  // Auth headers helper
  const getAuthHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(admin?.access_token && { 'Authorization': `Bearer ${admin.access_token}` })
  }), [admin?.access_token]);

  // Theme preview functions
  const startPreview = useCallback((previewTheme) => {
    if (originalDarkMode === null) {
      setOriginalDarkMode(isDarkMode);
    }
    setPreviewMode(previewTheme);
    
    if (previewTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    toast.info(`Previewing ${previewTheme} mode - Click "Exit Preview" to restore`, { duration: 3000 });
  }, [isDarkMode, originalDarkMode]);

  const exitPreview = useCallback(() => {
    if (originalDarkMode !== null) {
      if (originalDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      toast.success('Theme restored');
    }
    setPreviewMode(null);
    setOriginalDarkMode(null);
  }, [originalDarkMode]);

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/admin/login', loginData);
      const data = res.data;
      setAdmin(data);
      localStorage.setItem('bulaccino-admin', JSON.stringify(data));
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    }
    setLoading(false);
  };

  // Logout handler
  const handleLogout = useCallback(() => {
    setAdmin(null);
    localStorage.removeItem('bulaccino-admin');
    toast.success('Logged out successfully');
  }, []);

  // Categories handler
  const handleCategoriesChange = useCallback((cats) => {
    setCategories(cats);
    const labels = {};
    cats.forEach(c => {
      labels[c.id] = c.name;
    });
    setCategoryLabels(labels);
  }, []);

  // Fetch data based on active tab
  const fetchData = useCallback(async () => {
    if (!admin) return;
    
    try {
      if (['menu', 'dashboard', 'specials', 'build-your-own', 'inventory'].includes(activeTab)) {
        const res = await api.get(`/menu?include_unavailable=true&_t=${Date.now()}`);
        setMenuItems(res.data);
      }
      if (['orders', 'dashboard'].includes(activeTab)) {
        const res = await api.get(`/admin/orders?_t=${Date.now()}`);
        setOrders(res.data);
      }
      if (['messages', 'dashboard'].includes(activeTab)) {
        const res = await api.get(`/admin/messages?_t=${Date.now()}`);
        setMessages(res.data);
      }
      if (activeTab === 'users') {
        const res = await api.get(`/admin/users?_t=${Date.now()}`);
        setAdminUsers(res.data);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please log in again.');
      }
    }
  }, [admin, activeTab, handleLogout]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get(`/menu/categories?_t=${Date.now()}`);
        handleCategoriesChange(res.data);
      } catch (err) {
        console.error('Failed to fetch categories');
      }
    };
    fetchCategories();
  }, [handleCategoriesChange]);

  // Fetch data when admin or tab changes
  useEffect(() => {
    if (admin) {
      fetchData();
    }
  }, [admin, activeTab, fetchData]);

  // Menu item handlers
  const handleEditMenuItem = useCallback((item) => {
    setEditingItem(item);
    setShowMenuModal(true);
  }, []);

  const handleAddMenuItem = useCallback((defaultCategory) => {
    setEditingItem(null);
    setDefaultMenuCategory(defaultCategory);
    setShowMenuModal(true);
  }, []);

  // Login screen
  if (!admin) {
    return (
      <div className="min-h-screen bg-muted/30 dark:bg-gray-900 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-card p-8 rounded-3xl shadow-xl max-w-md w-full"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <LayoutDashboard className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-heading text-3xl text-foreground">Admin Login</h1>
            <p className="text-muted-foreground mt-2">Bulaccino Cafe Management</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="admin@bulaccinocafe.com"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">Password</Label>
              <Input
                id="password"
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter password"
                required
                data-testid="admin-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full rounded-full bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Main admin panel
  return (
    <PermissionsProvider admin={admin} authHeaders={getAuthHeaders()}>
      <div className="min-h-screen bg-background">
        {/* Mobile Header */}
        <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-[60] flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2"
              data-testid="mobile-menu-btn"
            >
              <MenuIcon className="w-6 h-6" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Coffee className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading text-sm text-foreground">Admin</span>
            </div>
          </div>
          <div className="text-xs text-muted-foreground truncate max-w-[100px]">
            {admin.name}
          </div>
        </header>

        {/* Mobile Overlay */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-50"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <AdminSidebar
          admin={admin}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
          navSections={NAV_SECTIONS}
          previewMode={previewMode}
          isDarkMode={isDarkMode}
          startPreview={startPreview}
          exitPreview={exitPreview}
          onLogout={handleLogout}
        />

        {/* Preview Mode Floating Banner */}
        {previewMode && (
          <div className="fixed top-16 lg:top-0 left-0 lg:left-64 right-0 z-40 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-4 flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-2 lg:gap-3 flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-shrink-0">
                {previewMode === 'light' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                <span className="font-medium text-sm lg:text-base">Preview: {previewMode.charAt(0).toUpperCase() + previewMode.slice(1)}</span>
              </div>
              <span className="text-amber-100 text-xs lg:text-sm hidden sm:inline truncate">• Viewing as customers</span>
            </div>
            <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
              <a 
                href="/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-sm transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Open Site
              </a>
              <button
                onClick={exitPreview}
                className="flex items-center gap-1 px-3 py-1 bg-white text-amber-600 hover:bg-amber-50 rounded-full text-xs lg:text-sm font-medium transition-colors"
              >
                <X className="w-3 h-3" />
                Exit
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className={`pt-16 lg:pt-0 lg:ml-64 p-4 lg:p-8 ${previewMode ? 'pt-28 lg:pt-12' : ''}`}>
          <AdminContent
            activeTab={activeTab}
            admin={admin}
            authHeaders={getAuthHeaders()}
            menuItems={menuItems}
            orders={orders}
            messages={messages}
            adminUsers={adminUsers}
            categories={categories}
            categoryLabels={categoryLabels}
            fetchData={fetchData}
            onEditMenuItem={handleEditMenuItem}
            onAddMenuItem={handleAddMenuItem}
            handleCategoriesChange={handleCategoriesChange}
          />
        </main>

        {/* Menu Item Modal */}
        <MenuItemModal
          isOpen={showMenuModal}
          onClose={() => { setShowMenuModal(false); setEditingItem(null); setDefaultMenuCategory('coffee'); }}
          item={editingItem}
          defaultCategory={defaultMenuCategory}
          categories={categories}
          onSave={fetchData}
        />
      </div>
    </PermissionsProvider>
  );
}
